import { useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import type { VendorSearchInput } from '@repo/api-contract';
import { useDispatch, useSelector } from '../../../../state';
import { useTranslation } from 'react-i18next';
import { buttonMarginSpace, buttonWidth } from '../../constants/form';
import CloseIcon from '@mui/icons-material/Close';
import {
  closeModal,
  searchVendors,
} from '../../../../state/vendorSearchModalSlice';
import DraggablePaper from '../../DraggablePaper';
import Table from './Table';
import { initialPageSize } from './constants';

export default function VendorSearchModal() {
  const { t: tGlobal } = useTranslation([], {
    keyPrefix: 'team.Global.Buttons',
  });
  const { t: tMIS } = useTranslation([], {
    keyPrefix: 'team.Modals.VendorsSearch',
  });

  const {
    selectedVendors,
    multiSelect,
    openVendorSearchModal,
    loading,
    onImport,
    onCancel,
  } = useSelector(({ vendorSearchModalSlice }) => vendorSearchModalSlice);
  const { team } = useSelector(({ userSlice }) => userSlice);

  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(closeModal());
    if (onCancel) onCancel();
  };
  const importVendors = () => {
    dispatch(closeModal());
    if (onImport) onImport(selectedVendors);
  };

  const initialResults = async () => {
    const payload: VendorSearchInput = {
      query: '',
      searchBy: 'vendorName',
      size: initialPageSize,
      page: 0,
    };
    await dispatch(searchVendors(payload));
  };

  useEffect(() => {
    if (!openVendorSearchModal || team === 'init' || team === 'loading') return;
    const fetchData = async () => {
      await initialResults();
    };
    fetchData();
  }, [openVendorSearchModal, team]);

  return (
    <Dialog
      open={openVendorSearchModal}
      onClose={(event, reason) => {
        if (reason && reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="lg"
      scroll="paper"
      PaperProps={{ className: 'w-full' }}
      PaperComponent={DraggablePaper}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{ m: 0, p: 1, cursor: 'move' }}
      >
        {tMIS('Browse Vendor')}
      </DialogTitle>
      <IconButton
        disabled={loading}
        color="primary"
        sx={{ position: 'absolute', right: 4, top: 4 }}
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ p: 1 }}>
        {loading ? (
          <Box
            height={800}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress size={80} />
          </Box>
        ) : (
          <Box sx={{ height: 800, width: '100%' }}>
            <Table multiSelect={multiSelect} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Box width="100%" sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button
            disabled={loading}
            variant="outlined"
            color="primary"
            sx={{ width: buttonWidth, marginRight: buttonMarginSpace }}
            onClick={handleClose}
          >
            {tGlobal('Cancel')}
          </Button>
          <Button
            disabled={loading}
            variant="contained"
            color="primary"
            sx={{ width: buttonWidth }}
            onClick={importVendors}
          >
            {loading ? <CircularProgress size={20} /> : tGlobal('Import')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
